import BookingForm from "@/components/BookingForm";
import SharedNavbar from "@/components/SharedNavbar";
import { CheckCircle2, Truck, Package, Home, Phone, ArrowRight, ShieldCheck, Clock3 } from "lucide-react";

const LOGO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/ds_logo_transparent_1343324419e041449c280c0bfa45d1f3.png";



// Çmimet mesatare zvicerane për Umzug/Zügeln
const pricing = [
  {
    category: "Kleiner Umzug",
    desc: "1–2 Zimmer, innerorts",
    price: "CHF 600–1'000",
    unit: "pauschal",
    details: ["2 Personen Team", "bis 4 Stunden", "Transporter inklusive", "Möbelschutz inklusive"],
  },
  {
    category: "Mittlerer Umzug",
    desc: "3–4 Zimmer, regional",
    price: "CHF 1'200–2'500",
    unit: "pauschal",
    details: ["3 Personen Team", "bis 8 Stunden", "LKW inklusive", "Kartonmaterial auf Wunsch"],
  },
  {
    category: "Grosser Umzug",
    desc: "5+ Zimmer / Gewerbe",
    price: "CHF 2'500–5'000",
    unit: "pauschal",
    details: ["4+ Personen Team", "ganztägiger Einsatz", "LKW + Hebebühne", "Komplettservice möglich"],
  },
  {
    category: "Stundenansatz",
    desc: "Flexibel nach Aufwand",
    price: "CHF 90–130",
    unit: "/ Stunde / Person",
    details: ["Transparent & fair", "Keine Mindestzeit", "Kurzfristig buchbar", "Ideal für Teilumzüge"],
  },
];

const included = [
  "Professionelles Zügelmaterial",
  "Möbelschutz & Decken",
  "Treppenhausschutz",
  "Ab- und Aufbau auf Wunsch",
  "Entsorgung auf Wunsch",
  "Termingarantie",
];

const steps = [
  { num: "01", title: "Kostenlose Offerte", text: "Schildern Sie uns Ihr Vorhaben — wir erstellen Ihnen eine unverbindliche Offerte, meist noch am gleichen Tag." },
  { num: "02", title: "Fixtermin buchen", text: "Wir reservieren Ihren Wunschtermin und stellen das passende Team zusammen." },
  { num: "03", title: "Professionelles Zügeln", text: "Unser Team kommt pünktlich, schützt Ihre Möbel und transportiert alles sicher ans Ziel." },
  { num: "04", title: "Fertig — Sie geniessen", text: "Möbel stehen, alles ist sauber. Wir hinterlassen Ihnen eine bezugsfertige Wohnung." },
];

export default function Zuegel() {
  return (
    <div className="min-h-screen bg-background">
      <SharedNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 py-16 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/80">
              <Truck className="h-3.5 w-3.5 text-red-400" />
              Umzug & Zügeln
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
              Stress-frei zügeln —<br />
              <span className="text-red-400">wir machen das für Sie</span>
            </h1>
            <p className="mt-5 text-lg text-white/75 leading-relaxed">
              Von der kleinen Wohnung bis zum grossen Firmenumzug — DS Logistik zügelt professionell, pünktlich und zum Fixpreis in der ganzen Schweiz.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <button
                onClick={() => { const el = document.getElementById("termin-anfragen"); if(el){ el.scrollIntoView({behavior:"smooth",block:"start"}); setTimeout(()=>{ const f = document.getElementById("booking-name"); if(f) f.focus(); }, 600); }}}
                className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-bold shadow-lg hover:-translate-y-px transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>
                Jetzt anfragen
              </button>
              {["Professionelles Team","Fixpreise","Ganzes Land","Kurzfristig möglich"].map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-white/80">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />{f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Was wir bieten */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Unser Zügel-Service</p>
              <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl leading-tight">
                Ihr Umzug in guten Händen
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed text-base">
                Ein Umzug ist mehr als nur Kisten schleppen. DS Logistik begleitet Sie von der ersten Planung bis zum letzten Karton — sorgfältig, termingerecht und mit dem richtigen Equipment. Unser erfahrenes Zügel-Team trägt Ihre Möbel schonend, schützt Treppenhäuser und stellt auf Wunsch auch gleich alles am neuen Standort auf.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed text-base">
                Ob Privatumzug, Seniorenumzug oder Firmenumzug — wir passen uns Ihren Bedürfnissen an. Flexibel, zuverlässig und zu transparenten Schweizer Preisen.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  { icon: Truck,       text: "Eigener Fuhrpark — Transporter bis LKW mit Hebebühne" },
                  { icon: Package,     text: "Professionelles Zügelmaterial, Möbelschutz & Kartonagen" },
                  { icon: Home,        text: "Ab- und Aufbau der Möbel am neuen Standort" },
                  { icon: ShieldCheck, text: "Versichert für alle Zügeltransporte in der Schweiz" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="text-sm text-muted-foreground leading-6">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tiparet vizuale */}
            <div className="rounded-3xl bg-primary/5 border border-primary/20 p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  { emoji: "🏠", title: "Privatumzug", text: "Von der kleinen Wohnung bis zum Einfamilienhaus — wir zügeln sorgfältig und termingerecht." },
                  { emoji: "🏢", title: "Firmenumzug", text: "Büros, Gewerbe und Lager — diskret, effizient und mit minimalem Unterbruch." },
                  { emoji: "👴", title: "Seniorenumzug", text: "Rücksichtsvoll, geduldig und mit besonderer Sorgfalt für ältere Personen." },
                  { emoji: "📦", title: "Teilumzug", text: "Einzelne Möbel oder Zimmer — kein Auftrag zu klein, wir kommen auch für Weniges." },
                ].map(({ emoji, title, text }) => (
                  <div key={title} className="flex gap-3">
                    <span className="text-2xl shrink-0">{emoji}</span>
                    <div>
                      <p className="font-bold text-sm text-foreground">{title}</p>
                      <p className="mt-1 text-xs text-muted-foreground leading-5">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preise */}
      <section className="py-20 bg-muted/30 border-y border-border/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Transparente Preise</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">Zügel-Preise Schweiz</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Marktübliche Schweizer Preise — transparent, fair und ohne versteckte Kosten. Alle Preise inklusive Zügelmaterial und Möbelschutz.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pricing.map((p) => (
              <div key={p.category} className="rounded-3xl border border-border/60 bg-card p-6 hover:border-primary/30 hover:-translate-y-1 transition-all duration-200" style={{ boxShadow: "0 16px 40px -24px rgba(37,10,8,0.18)" }}>
                <div className="inline-flex rounded-2xl bg-primary/10 p-2.5 text-primary mb-4">
                  <Truck className="h-5 w-5" />
                </div>
                <h3 className="text-base font-bold text-foreground">{p.category}</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-3">{p.desc}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-mono text-2xl font-black text-primary leading-none">{p.price}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">{p.unit}</p>
                <div className="border-t border-border/50 pt-4 space-y-1.5">
                  {p.details.map((d) => (
                    <div key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
                      {d}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Hinweis */}
          <div className="mt-8 rounded-2xl bg-amber-50 border border-amber-200 px-6 py-4">
            <p className="text-sm text-amber-800 leading-relaxed">
              <span className="font-bold">Hinweis:</span> Die angezeigten Preise sind Richtwerte basierend auf marktüblichen Schweizer Zügeltarifen. Der effektive Preis hängt von Entfernung, Etage, Menge und gewünschtem Service ab. Fordern Sie jetzt Ihre persönliche Offerte an — kostenlos und unverbindlich.
            </p>
          </div>

          {/* Inklusive */}
          <div className="mt-6 rounded-3xl bg-primary/5 border border-primary/20 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <ShieldCheck className="h-10 w-10 shrink-0 text-primary" />
              <div>
                <h3 className="text-lg font-bold text-foreground">Im Preis immer inklusive:</h3>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  {included.map((i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> {i}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">So einfach geht's</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">Ihr Umzug in 4 Schritten</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map(({ num, title, text }) => (
              <div key={num} className="rounded-3xl border border-border/60 bg-card p-6" style={{ boxShadow: "0 12px 30px -18px rgba(37,10,8,0.16)" }}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-mono text-sm font-bold text-primary mb-4">
                  {num}
                </span>
                <h3 className="text-base font-bold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warum DS */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-3xl bg-primary/5 border border-primary/20 p-8 sm:p-12">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary mb-3">Warum DS Logistik</p>
                <h2 className="text-2xl font-bold text-foreground sm:text-3xl">Zuverlässig zügeln seit 2005</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed text-sm">
                  Mit über 20 Jahren Erfahrung in der Logistik und einem eigenen Fuhrpark sind wir der ideale Partner für Ihren Umzug — egal ob klein oder gross, privat oder gewerblich. Wir kennen die Schweiz und liefern termingerecht.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  "20+ Jahre Erfahrung in der Schweiz",
                  "Eigener Fuhrpark — kein Drittanbieter",
                  "Versicherter Transport",
                  "Fixpreise — keine Überraschungen",
                  "Kurzfristige Termine möglich",
                  "Gesamte Schweiz abgedeckt",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      
      {/* Termin & Anfrage */}
      <section className="py-16 bg-muted/30 border-y border-border/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Jetzt buchen</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground">Termin anfragen</h2>
            <p className="mt-3 text-muted-foreground">Wählen Sie Service, Datum und Uhrzeit — wir melden uns innerhalb von 24 Stunden.</p>
          </div>
          <BookingForm defaultService="Zügeln / Umzug" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="mx-auto max-w-2xl px-5 lg:px-8 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-red-400 mb-3">Jetzt anfragen</p>
          <h2 className="text-3xl font-bold">Umzug planen?</h2>
          <p className="mt-3 text-white/70">Kostenlose Offerte in wenigen Minuten — rufen Sie uns an oder schreiben Sie uns.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="tel:+41326712010" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-[0_8px_24px_-10px_rgba(200,25,11,0.7)] hover:-translate-y-px transition-all">
              <Phone className="h-4 w-4" /> 032 671 20 10
            </a>
            <a href="https://wa.me/41798883262" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white hover:scale-105 transition-transform">
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            <a href="mailto:andre.stalder@ds-logistik.ch?subject=Umzugsanfrage"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
              <ArrowRight className="h-4 w-4 text-red-400" /> Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-background py-6 text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} DS Logistik · Bielstrasse 20, 4500 Solothurn ·{" "}
          <a href="/#/impressum" className="hover:text-primary">Impressum</a>
        </p>
      </footer>

      {/* WhatsApp floating */}
      <a href="https://wa.me/41798883262" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg hover:scale-105 transition-transform">
        <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="text-sm font-bold">WhatsApp</span>
      </a>
    </div>
  );
}
